import { useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PayPalButtons } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PayPalCheckout — Smart Buttons for both one-time orders AND recurring subscriptions.
 *   PayPalScriptProvider is mounted once in App.js.
 *
 * Props:
 *   orderPayload:  { package_id? , lookup_key?, quantity?, customer_email?, customer_name? } — REQUIRED
 *   subscription:  boolean — if true, uses PayPal Billing Plans (recurring). orderPayload must include package_id
 *   onSuccess:     called with capture/activation response on success
 *   disabled:      if true, blocks click through toast
 *   testId:        optional data-testid for the wrapper
 *   validate:      optional function returning true/false; if false, blocks & shows toast
 *   validateMsg:   message when validate returns false
 *   height:        button height (default 45)
 *   layout:        "horizontal" | "vertical" (default "horizontal")
 */
export default function PayPalCheckout({
  orderPayload,
  subscription = false,
  onSuccess,
  disabled = false,
  testId = "paypal-checkout",
  validate,
  validateMsg = "Please complete the form before continuing.",
  height = 45,
  layout = "horizontal",
}) {
  const style = useMemo(
    () => ({ layout, color: "gold", shape: "pill", label: subscription ? "subscribe" : "paypal", tagline: false, height }),
    [layout, height, subscription]
  );

  const commonProps = {
    style,
    disabled,
    forceReRender: [JSON.stringify(orderPayload), disabled, subscription],
    onClick: (_data, actions) => {
      if (validate && !validate()) {
        toast.error(validateMsg);
        return actions.reject();
      }
      return actions.resolve();
    },
    onError: (err) => {
      console.error("PayPal error:", err);
      toast.error("PayPal encountered an error. Try again or use card checkout.");
    },
    onCancel: () => {
      toast.message("Cancelled.");
    },
  };

  if (subscription) {
    return (
      <div data-testid={testId} className="paypal-checkout-wrapper">
        <PayPalButtons
          {...commonProps}
          createSubscription={async () => {
            try {
              const body = {
                package_id: orderPayload.package_id,
                origin_url: window.location.origin,
                customer_email: orderPayload.customer_email,
                customer_name: orderPayload.customer_name,
              };
              const res = await axios.post(`${API}/paypal/create-subscription`, body);
              return res.data.subscription_id;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "PayPal subscription is temporarily unavailable.");
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await axios.get(`${API}/paypal/subscription-status/${data.subscriptionID}`);
              toast.success("Subscription activated — welcome aboard.");
              if (onSuccess) onSuccess(res.data);
              else window.location.href = `/payment-success?paypal_subscription_id=${data.subscriptionID}`;
            } catch (e) {
              toast.error("Activation status pending — check your email for PayPal confirmation.");
            }
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId} className="paypal-checkout-wrapper">
      <PayPalButtons
        {...commonProps}
        createOrder={async () => {
          try {
            const body = {
              ...orderPayload,
              origin_url: window.location.origin,
              quantity: orderPayload.quantity || 1,
            };
            const res = await axios.post(`${API}/paypal/create-order`, body);
            return res.data.order_id;
          } catch (e) {
            toast.error(e?.response?.data?.detail || "PayPal is temporarily unavailable.");
            throw e;
          }
        }}
        onApprove={async (data) => {
          try {
            const res = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
            if (res.data.payment_status === "paid") {
              toast.success("Payment received — thank you.");
              if (onSuccess) onSuccess(res.data);
              else window.location.href = `/payment-success?paypal_order_id=${data.orderID}`;
            } else {
              toast.error(`Payment status: ${res.data.payment_status}`);
            }
          } catch (e) {
            toast.error("Capture failed — please contact support.");
          }
        }}
      />
    </div>
  );
}
